<script setup lang="ts">
import { type ModelRef, watch } from "vue";
import uploadUtils, {
  type FilesUploadErrorMessagesI,
} from "~/core/utils/upload.utils";

defineProps<{
  isLoading: boolean;
}>();

const show: ModelRef<boolean> = defineModel<boolean>("show", {
  required: true,
});

const emits = defineEmits(["upload-id-card"]);

const { t } = useI18n();

const files: Ref<FileList | undefined | null> = ref();
const fileProgress: Ref<number | undefined | null> = ref();
const fileError: Ref<Error | undefined | null> = ref();
const fileFormat = [".json"];
const fileSelected: ComputedRef<boolean> = computed(
  () => !!files.value?.length,
);
const MAX_FILE_SIZE_BYTES_ALLOWED = 750_000_000;
const errorMessages: FilesUploadErrorMessagesI = {
  invalidType: t(
    "administration.relations.uploadIdCard.validation.fileMandatory",
  ),
  empty: t("administration.relations.uploadIdCard.validation.fileEmpty"),
  size: t("administration.relations.uploadIdCard.validation.fileSize"),
};

async function uploadIdCard() {
  fileError.value = fileError.value !== undefined ? null : undefined;
  if (
    files.value &&
    files.value.length &&
    uploadUtils.canUploadFile(
      files.value[0],
      MAX_FILE_SIZE_BYTES_ALLOWED,
      fileFormat,
      errorMessages,
      fileError,
    )
  ) {
    emits("upload-id-card", { file: files.value[0], progress: fileProgress });
  }
}

function reset() {
  files.value = undefined;
  fileProgress.value = undefined;
  fileError.value = undefined;
}

function onClose() {
  show.value = false;
  reset();
}

// Force reset when modal is opening
watch(
  () => show.value,
  () => {
    if (show.value) {
      reset();
    }
  },
);
</script>

<template>
  <lxc-common-modal
    :show="show"
    body-class="bg-white"
    header-class="!justify-start"
    footer-class="!flex-row-reverse gap-3"
    class="!w-[34rem]"
    no-close-button
    @close="onClose"
  >
    <template #header>
      <h4>
        {{ t("administration.relations.uploadIdCard.title") }}
      </h4>
    </template>
    <template #body>
      <upload-file
        v-model:files="files"
        v-model:files-progress="fileProgress"
        v-model:files-upload-error="fileError"
        :file-formats="fileFormat"
      />
    </template>
    <template #footer>
      <lxc-button
        :title="t('button.upload')"
        :text="t('button.upload')"
        :disabled="!fileSelected"
        :is-loading="isLoading"
        @click="uploadIdCard"
      />
      <lxc-button
        :title="t('button.cancel')"
        :text="t('button.cancel')"
        type="tertiary"
        @click="onClose"
      />
    </template>
  </lxc-common-modal>
</template>
